import { useTranslation } from 'react-i18next';
import { Skeleton, Text } from '@mantine/core';
import BestPriceLabel from '@/components/common/BestPriceLabel';
import BestPriceLink from '@/components/common/BestPriceLink';
import { bestPriceTone } from '@/lib/bestOffer';
import type { BestPriceEntry } from '@/types/buy';

interface BestPriceCellProps {
  /** Лучшее предложение продажи под мою строку; undefined — предложений нет. */
  readonly entry?: BestPriceEntry;
  /** Моя цена строки (CPT, ₽/т) по этой предоплате — для оценки выгоды (купить дешевле моей). */
  readonly myPrice?: number | null;
  /** Идёт загрузка лучших цен (показываем скелет вместо «нет предложений»). */
  readonly loading?: boolean;
  /** Лучшие цены не загрузились (ошибка ≠ «нет предложений» — не врём пользователю). */
  readonly unavailable?: boolean;
  /** Ссылка на встречное предложение продавца — делает цену кликабельным CTA «Купить». */
  readonly buyTo?: string;
}

// Рынок рядом с моей ценой в гриде «Купить». Есть предложение → единый CTA «Купить по X» (оранжевая
// кнопка «лучшей цены») с дельтой выгоды (купить дешевле моей цены = выигрыш, +N зелёный ↗; дороже =
// потеря, −N красный ↘). Привязка к моей цене есть (myPrice), поэтому выгоду считаем здесь:
// advantage = myPrice − рыночная цена (>0 — дешевле моей = выигрыш).
// Без предложения — вторичный текст. Раскладку (лучшая цена ПЕРЕД моей) задаёт родитель (PriceGrid).
export default function BestPriceCell({ entry, myPrice, loading, unavailable, buyTo }: BestPriceCellProps) {
  const { t } = useTranslation();

  if (loading) {
    return <Skeleton h={20} w={130} />;
  }

  if (!entry) {
    return (
      <Text size="sm" c="gray.6">
        {t(unavailable ? 'app.buy.marketUnavailable' : 'app.buy.noMarket')}
      </Text>
    );
  }

  const advantage = myPrice != null ? myPrice - entry.price : undefined;

  return buyTo ? (
    <BestPriceLink to={buyTo} price={entry.price} action="buy" tone={bestPriceTone(advantage)} advantage={advantage} />
  ) : (
    // Защитный фолбэк: предложение есть, но ссылку построить не удалось — показываем цену без перехода.
    <BestPriceLabel price={entry.price} action="buy" tone={bestPriceTone(advantage)} advantage={advantage} />
  );
}
